import React from "react";
import styled from "styled-components";

import { theme } from "../../../styles/theme";

type SharedProps = {
  accept?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
};
interface DropZoneProps extends SharedProps {
  children: React.ReactNode;
}
interface EmptyZoneProps extends SharedProps {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  isDropZone?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}

function DropZone(props: DropZoneProps) {
  const { children, accept, onChange } = props;
  const [isDropping, setIsDropping] = React.useState(false);
  const uniqueId = React.useMemo(
    () => window.crypto.getRandomValues(new Uint32Array(1))[0].toString(36).substring(7),
    [],
  );
  return (
    <DropZoneLabel htmlFor={uniqueId} isDropping={isDropping}>
      {children}
      <DropZoneInput
        onDragEnter={() => setIsDropping(true)}
        onDragLeave={() => setIsDropping(false)}
        id={uniqueId}
        accept={accept}
        onChange={(event) => onChange?.(event)}
        type="file"
      />
    </DropZoneLabel>
  );
}

export default function EmptyZone(props: EmptyZoneProps) {
  const {
    icon,
    title,
    subtitle,
    isDropZone,
    disabled,
    accept = "application/pdf,image/jpg,image/jpeg,image/png",
    onChange,
    onClick,
  } = props;
  const Content = (
    <>
      {icon}
      <Title>{title}</Title>
      <Subtitle>{subtitle}</Subtitle>
    </>
  );

  return (
    <ComponentWrapper disabled={disabled}>
      {isDropZone ? (
        <DropZone accept={accept} onChange={(event) => onChange?.(event)}>
          {Content}
        </DropZone>
      ) : (
        <Box type="button" disabled={disabled} onClick={() => onClick?.()}>
          {Content}
        </Box>
      )}
    </ComponentWrapper>
  );
}

const ComponentWrapper = styled.div<{ disabled?: boolean }>`
  width: 100%;
  ${({ disabled }) => (disabled ? "opacity: 0.5;" : "")}
  ${({ disabled }) => (disabled ? "point-event: none" : "")}
`;

/* The pale blue panel of the redesign: the invitation reads as an area to act in, not as a card. */
const WrapperStyle = `
cursor: pointer;
width: 100%;
display: flex;
flex-direction: column;
align-items: center;
justify-content: center;
padding: 40px 30px;
border-radius: 12px;
background-color: #e8f2fd;
&:hover {
	background-color: ${theme.colors.blue[200]};
}`;

const Box = styled.button`
  border: 0;
  font: inherit;
  text-align: inherit;
  ${WrapperStyle}
`;

const Title = styled.span`
  ${theme.mediumText};
  margin-top: 16px;
  text-align: center;
  font-size: 16px;
  line-height: 20px;
  color: #741537;
`;

const Subtitle = styled.span`
  ${theme.text};
  margin-top: 8px;
  max-width: 420px;
  text-align: center;
  font-size: 12px;
  line-height: 15px;
  color: ${theme.colors.gray[800]};
`;

const DropZoneLabel = styled.label<{ isDropping?: boolean }>`
  position: relative;
  display: block;
  width: 100%;
  border: dashed 2px transparent;
  ${WrapperStyle}
  ${(props) =>
    props.isDropping &&
    `
		background-color: ${theme.colors.blue[200]};
		border-color: ${theme.colors.blue[700]};
		`}
`;

const DropZoneInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
`;
